<template>
  <div class="layout-regions">
    <MobileHeader>
      <template #action>
        <OperationTypeNavigationMobile :showHmePolicy="hasHME" />
      </template>
      <SearchIcon
        :aria-label="$t('general.search')"
        class="icon"
        @click="openMobileSearch"
      />
    </MobileHeader>
    <!-- Mobile select -->
    <div
      v-if="setGroupsWithIssues?.length !== 0"
      class="layout-regions__header hide-for-medium"
    >
      <Popper
        v-model:open="menuOpen"
        placement="bottom-end"
      >
        <div class="menu menu--dropdown">
          <IssueOverviewNavigation :setsWithIssues="setGroupsWithIssues" />
        </div>
        <template #trigger>
          <div class="typo-small set-group-navigation__trigger">
            {{ currentGroupSelected }}
            <ArrowDown16Svg />
          </div>
        </template>
      </Popper>
      <SwitchButton
        v-model="currentView"
        :items="viewOptions"
        @update:model-value="refreshView"
      />
    </div>

    <!-- Desktop side nav -->
    <div class="layout-regions__regions show-for-medium">
      <OperationTypeNavigation :showHmePolicy="hasHME" />
      <!-- Empty state -->
      <EmptyMessage
        v-if="!groupedSetsLoading && setGroupsWithIssues?.length === 0"
        :message="$t('empty_states.sets.no_issues')"
        :modifiers="['grow', 'center']"
      />
      <!-- Loading state -->
      <div
        v-if="groupedSetsLoading"
        class="set-group-navigation"
      >
        <SkeletonElement
          v-for="index in 3"
          :key="index"
          :height="20"
          :style="'marginBottom: 8px'"
        />
      </div>
      <!-- Content -->
      <nav
        v-if="!groupedSetsLoading && setGroupsWithIssues?.length > 0"
        class="set-group-navigation"
      >
        <p class="typo-ignore typo-tiny">
          {{ $t('shafts.sets').toUpperCase() }}
        </p>
        <IssueOverviewNavigation :setsWithIssues="setGroupsWithIssues" />
      </nav>
    </div>

    <NoticeMessage
      v-if="issuesStore.loadingNotification && issuesStore.loading"
      iconStyle="info"
      :message="$t('general.long_load_message')"
      :modifiers="['margin']"
    />
    <IssuesSkeletonLoader
      v-if="issuesStore.loading"
      class="show-for-medium"
    />
    <div
      v-if="!issuesStore.loading"
      :class="{ 'layout-regions__content': true }"
    >
      <div class="layout-regions__issues">
        <!-- Desktop search -->
        <div class="layout-regions__search-bar show-for-medium">
          <SearchIcon :class="{ icon: true, 'icon--darker': !issueSearch }" />
          <input
            v-model="issueSearch"
            class="layout-regions__search-input typo-small-title"
            :disabled="groupIssueList?.length === 0"
            :placeholder="$t('shafts.issues.search_issues')"
          >
          <button
            v-if="issueSearch"
            class="button button--tiny button--transparent"
            type="button"
            @click="issueSearch = ''"
          >
            <UseSvg
              class="icon icon--small"
              name="close_circle_16"
            />
          </button>
        </div>

        <div
          :class="{
            'layout-regions__issues-content': true,
            hide: issueSearch,
          }"
        >
          <IssuesSkeletonLoader v-if="!groupIssueList" />
          <p
            v-if="groupIssueList && groupIssueList?.length > 0"
            class="selected-name show-for-medium"
          >
            {{ currentGroupSelected }}
          </p>
          <div class="layout-regions__issues-header">
            <span
              v-if="groupIssueList"
              class="layout-regions__issues-count typo-small-nav-item"
            >
              {{ groupIssueList?.length }}
              {{ $t('activerecord.models.plants/issue', { count: groupIssueList?.length }) }}
            </span>
            <IssueFilter
              v-if="isActiveFilter || groupIssueList?.length > 0"
              :sortOptions="sortByOptions"
              :typeFilter="false"
              @set-filter-options="setFilterOptions"
            />
          </div>
          <RecyclerView
            v-if="groupIssueList && groupIssueList?.length > 0"
            ref="issueScrollView"
            :class="{
              'layout-regions__list': true,
              'show-for-medium': currentView !== 'list',
            }"
            :count="groupIssueList?.length"
            :gap="0.125"
            :height="7"
            hideScrollingX
            name="layout-regions__list-item-"
          >
            <template #default="{ index, style, key }">
              <IssueCard
                :key="key"
                isSmallCard
                :issue="groupIssueList[index]"
                :overrideIssuePath="getIssuePath($route)"
                :responsive="false"
                :showDescription="false"
                :style="style"
              />
            </template>
          </RecyclerView>
        </div>

        <div
          v-if="issueSearch"
          class="layout-regions__issues-content"
        >
          <EmptyMessage
            v-if="issueSearch && issueResults.length === 0"
            :message="$t('general.no_results')"
          >
            <UseSvg name="magnifying_glass_circle" />
          </EmptyMessage>
          <template v-if="issueSearch && issueResults.length > 0">
            <div class="layout-regions__results-header typo-small">
              {{ $t('general.results') }}
            </div>
            <div class="layout-regions__list scroll-container layout-regions__search-results">
              <ul class="recycler__list">
                <IssueCard
                  v-for="issue in issueResults"
                  :key="issue.id"
                  isSmallCard
                  :issue="issue"
                  :overrideIssuePath="getIssuePath($route)"
                  :responsive="false"
                  :showDescription="false"
                />
              </ul>
            </div>
          </template>
        </div>
      </div>

      <div
        :class="{
          'layout-regions__map layout-regions--map scroll-container': true,
          'show-for-medium': currentView !== 'main',
        }"
      >
        <ShaftOverview
          :groupedSetData="groupedSets"
          :loading="groupedSetsLoading"
          :numberOfSets="currentShaft?.number_of_sets"
          :stations="stations"
        />
      </div>
    </div>

    <!-- Mobile search -->
    <div
      v-if="mobileSearchOpen"
      class="layout-regions__mobile-search hide-for-medium"
    >
      <div class="layout-regions__search-bar">
        <SearchIcon class="icon" />
        <input
          ref="mobileSearchInput"
          v-model="mobileSearch"
          :aria-label="$t('general.search')"
          class="layout-regions__search-input typo-small-title"
          placeholder="Search issues"
        >
        <UseSvg
          class="icon"
          name="close_circle_24"
          @click="mobileSearchOpen = false"
        />
      </div>
      <EmptyMessage
        v-if="mobileSearch && issueResults.length === 0"
        :message="$t('general.no_results')"
      >
        <UseSvg name="magnifying_glass_circle" />
      </EmptyMessage>
      <ul
        class="layout-regions__search-results scroll-container recycler__list"
      >
        <IssueCard
          v-for="issue in issueResults"
          :key="issue.id"
          isSmallCard
          :issue="issue"
          :overrideIssuePath="getIssuePath($route)"
          :responsive="false"
          :showDescription="false"
        />
      </ul>
    </div>
  </div>
  <router-view v-if="!isIncluded(routerNameList, $route.name)" />
</template>

<script setup>
import { inject, computed, ref, watch, onMounted, onUnmounted, nextTick } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import useAjax from '@/functions/use_ajax';

// Utils
import { sortingFunctions, filterBy } from '@/functions/utils.js';
import { useActiveFilter } from '@/functions/composables/active_filter.js';
import { useViewOptions } from '@/functions/options/view_option_items';
import debounce from '@/vendor/debounce';
import I18n from 'i18n-js';
import pluralize from '@/functions/pluralize';
import Searcher from '@/functions/searcher';

// Stores
import { useIssuesStore } from '@/stores/issues/issuesStore';

// Components
import EmptyMessage from '@/components/generic/empty_message.vue';
import IssueCard from '@/components/issues/issue_card.vue';
import IssueFilter from '@/components/issues/issue_filter.vue';
import IssueOverviewNavigation from '@/components/shaftsV2/issues/issues_overview_navigation.vue';
import IssuesSkeletonLoader from '@/components/issues/issues_skeleton_loader.vue';
import MobileHeader from '@/components/generic/mobile_header.vue';
import OperationTypeNavigation from '@/components/shaftsV2/operations/operation_type_navigation.vue';
import OperationTypeNavigationMobile from '@/components/shaftsV2/operations/operation_type_navigation_mobile.vue';
import Popper from '@/components/generic/popper.vue';
import RecyclerView from '@/components/generic/recycler_view.vue';
import ShaftOverview from '@/components/shaftsV2/overview/shaft_overview.vue';
import SkeletonElement from '@/components/generic/skeleton_element.vue';
import SwitchButton from '@/components/form/switch_button.vue';
import NoticeMessage from '@/components/generic/notice_message.vue';

// Svg
import UseSvg from '@/components/generic/use_svg.vue';
import ArrowDown16Svg from '@/images/arrow-down-16.svg';
import SearchIcon from '@/images/search.svg';

// Set up store
const issuesStore = useIssuesStore();

const props = defineProps({
  issueType: { type: String, required: false, default: null },
  organisationId: { type: String, required: true },
  setGroup: { type: String, required: false, default: null },
  shaftId: { type: String, required: true },
  inspectionId: { type: String, required: false, default: null },
  issueId: { type: String, required: false, default: null },
  setGroupId: { type: String, required: false, default: null },
  setNumber: { type: String, required: false, default: null },
  setId: { type: String, required: false, default: null },
});

const { currentView, viewOptions } = useViewOptions();
const ajax = useAjax();
const currentShaft = inject('currentShaft');
const route = useRoute();
const router = useRouter();
const sortFunctions = sortingFunctions();

const groupedSets = ref([]);
const groupedSetsLoading = ref(false);
const selectedSet = ref(null);
const stations = ref(null);
const stationsLoading = ref(false);

const issues = ref(null);
const issuesLoading = ref(false);
let issuesSearcher = null;

const issueResults = ref([]);
const issueSearch = ref('');

const mobileSearch = ref('');
const mobileSearchInput = ref(null);
const mobileSearchOpen = ref(false);

const conditions = ref([]);
const priorities = ref([]);
const sortBy = ref('highest_risk');

const issueScrollView = ref();

const menuOpen = ref(false);
const timeOutWait = 300;
const routerNameList = ['shaftV2IssuesOverview', 'shaftV2IssuesOverviewGroup'];

const { isActiveFilter, validateFilterOptions } = useActiveFilter();

const hasHME = computed(() => {
  return currentShaft.value?.policy?.show_heavy_mobile_equipment;
});

const currentGroupSelected = computed(() => {
  return (
    groupedSets.value?.filter(
      (item) => item.id === route?.params?.setGroupId
    )[0]?.name || I18n.t('shafts.issues.all_sets')
  );
});

// Only return set groups that have issues
const setGroupsWithIssues = computed(() => {
  let withIssues = groupedSets.value?.filter((item) => item.issue_count !== 0);
  return withIssues
    ? withIssues.map((item) => ({
        value: item.id,
        label: item.name,
        total: `${pluralize(
          item.issue_count,
          I18n.t('activerecord.models.plants/issue.one'),
          I18n.t('activerecord.models.plants/issue.other')
        )}`,
      }))
    : [];
});

const BASE_URL = `/organisations/${props.organisationId}/shafts_v2/shafts/${props.shaftId}/overview`;

const ENDPOINTS = computed(() => {
  return {
    sets: `${BASE_URL}/sets`,
    stations: `${BASE_URL}/stations`,
    issues: `${BASE_URL}/shaft_issues`,
  };
});

onMounted(async () => {
  await fetchOverviewSets();
  await fetchOverviewStations();
  await fetchOverviewIssues();
  validateRouteParams(route.params.setGroupId);
  issuesSearcher = new Searcher(groupIssueList.value, { keys: ['title', 'id'] });
});

onUnmounted(() => {
  ajax.abort(); // Abort any ajax calls when leaving this component
});

watch(issuesStore, async() => {
  renderIssues();
});

watch(
  () => route.params,
  () => {
    validateRouteParams(route.params.setGroupId);
    resetIssuesByLocationUpdate();
  }
);

const allIssues = computed(() => {
  return issues?.value;
});

/**
 * Route fix for cases where the issue is the only one in the set group
 * and we update its location and set from the overlay,
 * the URL becomes invalid when we go back to the main page,
 * this method fetches new data and checks if the URL is still valid,
 * if it's not valid we load the page on 'all sets' panel option
 */
async function resetIssuesByLocationUpdate() {
  if (route.name !== 'shaftV2IssuesOverviewGroup') return;

  const issueWithUpdatedLocation = issuesStore.issues?.flatMap(group => group.issues).find(issue => !!issue?.location_update);
  if (issueWithUpdatedLocation) {
    await fetchOverviewSets();
    await fetchOverviewStations();
    await fetchOverviewIssues();

    const validRoute = setGroupsWithIssues.value.some((group) => group.value === route.params.setGroupId);
    if (!validRoute) {
      router.replace({ name: 'shaftV2IssuesOverview' });
    }
  }
}

// Returns issues by group id
function filterIssuesByGroupId(id) {
  return allIssues.value?.filter((issue) => issue.id === id)[0]?.issues;
}

// Returns all issues as a flat array
const allIssuesFlattened = computed(() => {
  let issues = [];

  allIssues.value?.forEach((issue) => {
    if (issue.issues.length > 0) {
      issues.push(...issue.issues);
    }
  });

  return issues;
});
// Returns all issues or filtered issues based on params
const issueList = computed(() => {
  if (route.params.setGroupId) {
    return filterIssuesByGroupId(route.params.setGroupId);
  } else {
    return allIssuesFlattened.value;
  }
});

// Returns the list to be used in the template
const groupIssueList = computed(() => {
  return filteredIssueList();
});

const sortByOptions = [
  { label: I18n.t('issues.highest_risk'), value: 'highest_risk' },
  { label: I18n.t('issues.lowest_risk'), value: 'lowest_risk' },
  { label: I18n.t('general.oldest'), value: 'oldest' },
  { label: I18n.t('general.newest'), value: 'newest' },
  {
    label: I18n.t('issues.highest_repair_priority'),
    value: 'highest_repair_priority',
  },
  {
    label: I18n.t('issues.lowest_repair_priority'),
    value: 'lowest_repair_priority',
  },
];

/**
 * Fetch sets
 */
async function fetchOverviewSets() {
  groupedSetsLoading.value = true;

  let { ok, data } = await ajax(ENDPOINTS.value.sets);

  if (ok) {
    groupedSets.value = data.grouped_sets;
  }

  groupedSetsLoading.value = false;
}

/**
 * Fetch stations
 */
async function fetchOverviewStations() {
  stationsLoading.value = true;

  let { ok, data } = await ajax(ENDPOINTS.value.stations);

  if (ok) {
    stations.value = data.stations;
  }

  stationsLoading.value = false;
}

function renderIssues() {
  issues.value = issuesStore.issues;
  refreshView();
}

/**
 * Fetch issues
 */
async function fetchOverviewIssues() {
  issuesLoading.value = true;
  issuesStore.$reset();

  setTimeout(() => {
    issuesStore.loadingNotification = true;
  }, 5000);

  let { ok, data } = await ajax(ENDPOINTS.value.issues);

  if (ok) {
    issuesStore.issues = data;
    renderIssues();
  }

  issuesLoading.value = false;
  issuesStore.setLoadingToFalse();
}

/**
 * Sets the current selectedSet based off the setGroupId
 * @param {string} setGroupId - setGroupId
 */
function setSelectedSet(setGroupId) {
  if (setGroupId) {
    selectedSet.value = setGroupId;
  }
}

watch(issueSearch, debounce(() => {
  if (!issueSearch.value) return;
  issueResults.value = issuesSearcher.search(issueSearch.value);
}, 300, true));

watch(mobileSearch, debounce(() => {
  if (!mobileSearchOpen.value || !mobileSearch.value) return;
  issueResults.value = issuesSearcher.search(mobileSearch.value);
}, 300, true));

/** Scrolls to the active data bubble */
function scrollToActiveGroup() {
  setTimeout(() => {
    document.querySelector('.overview-indicator--active')?.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'center',
    });
  }, timeOutWait);
}

/**
 * Check to see if the current route params is
 * found in the setGroupData, else 404
 * @param {string} setGroupId - current route setGroupId param
 */
function validateRouteParams(setGroupId) {
  if (!isIncluded(routerNameList, route.name)) return;
  issueResults.value = [];
  issueSearch.value = '';
  menuOpen.value = false;
  if (setGroupId) {
    let validRoute = setGroupsWithIssues.value.some((item) => item.value === setGroupId);

    if (validRoute) {
      setSelectedSet(setGroupId);
      scrollToActiveGroup();
    }

    if (!validRoute) {
      router.replace({ name: 'NotFound' });
    }
  } else {
    document.querySelector('.scroll-container').scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  }
}

// Watch selected group for mobile routing
watch(selectedSet, () => {
  if (!isIncluded(routerNameList, route.name)) return;
  router.replace({
    name: 'shaftV2IssuesOverviewGroup',
    params: { setGroupId: selectedSet.value },
  });
});

async function openMobileSearch() {
  mobileSearchOpen.value = true;
  mobileSearch.value = '';
  issueResults.value = [];
  await nextTick();
  mobileSearchInput.value.focus();
}

function setFilterOptions(options) {
  sortBy.value = options.sortOption;
  conditions.value = options.conditions;
  priorities.value = options.priorities;

  /* Check if filter has any active filter options */
  validateFilterOptions(options);
}

function filteredIssueList() {
  const issues = issueList.value;

  if (!issues) return null;

  let list = [...issues.values()];

  if (conditions.value.length > 0) {
    list = list.filter((i) => {
      return conditions.value.includes(i.overall_condition_rating);
    });
  }

  if (priorities.value.length > 0) {
    list = list.filter((i) => {
      return priorities.value.includes(i.priority);
    });
  }

  if (
    sortBy.value === 'highest_repair_priority' ||
    sortBy.value === 'lowest_repair_priority'
  ) {
    const highList = filterBy(list, 'priority', 'X');
    const lowList = filterBy(list, 'priority', 'M');
    const numberList = filterBy(list, 'priority')?.sort(
      sortFunctions[sortBy.value]
    );
    let finalList = [];

    if (sortBy.value === 'highest_repair_priority') {
      finalList = [...highList, ...numberList, ...lowList];
    } else if (sortBy.value === 'lowest_repair_priority') {
      finalList = [...lowList, ...numberList, ...highList];
    }
    return finalList;
  } else {
    return list.sort(sortFunctions[sortBy.value]);
  }
}

async function refreshView() {
  switch (currentView.value) {
    case 'main':
      scrollToActiveGroup();
      break;
    case 'list':
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, timeOutWait);
      break;
  }
}

function isIncluded(list, name) {
  return list.includes(name);
}

function getIssuePath(route) {
  return route.params.setGroupId ? 'shaftV2RegionsIssuesTypeOverviewGroupId' : 'shaftV2RegionsIssuesTypeOverviewGroup';
}
</script>

<style scoped lang="scss">
@import '@/stylesheets/global';

.overview-container {
  padding-top: bu(2);

  @include breakpoint(medium) {
    padding-top: bu(5);
  }
}

.set-group-navigation {
  padding: bu(1);

  p {
    color: map-get($foreground-color, default);
    margin-bottom: bu(1);
  }

  &__trigger {
    display: flex;

    svg {
      color: map-get($foreground-color, dark);
    }
  }
}

.selected-name {
  font-weight: 500;
  margin: bu(1) bu(1.4);
}
</style>
